import type { FeatureIconKey } from './featureIconTypes';

export interface WhenToMovePoint {
  id: string;
  /** Short pain — shown bold. */
  headline: string;
  /** One-line consequence — shown muted. */
  detail: string;
}

export interface PartnershipPillar {
  id: string;
  title: string;
  iconKey: FeatureIconKey;
  description: string;
  stat?: string;
}

/** One row: what the business asks, what Crowpeaks modules answer it. */
export interface FeatureBusinessRow {
  id: string;
  iconKey: FeatureIconKey;
  businessAsk: string;
  modules: string[];
}

export interface FeatureBusinessGroup {
  id: string;
  label: string;
  rows: FeatureBusinessRow[];
}

export interface LandingFeatureCard {
  id: string;
  iconKey: FeatureIconKey;
  title: string;
  description: string;
}

export const LANDING_HERO = {
  eyebrow: 'CRM for growing sales teams',
  title: 'See how your business and sales',
  titleAccent: 'are really doing.',
  subtitle:
    'One workspace for pipeline, customers, quotes, commissions, and team activity — so owners and sales leaders stop chasing spreadsheets.',
  customization:
    'No large upfront cost — 20% to start, then flexible monthly, quarterly, or half-yearly payments.',
};

export const LANDING_CTA_BAND = {
  title: 'Ready to run sales from one system?',
  lead: 'Request access and we will review your organization before enabling sign-in.',
  primaryLabel: 'Request access',
  secondaryLabel: 'See how we partner',
};

export const PARTNERSHIP = {
  eyebrow: 'What makes us different',
  title: 'Why teams work with us',
  lead: 'Partnership and guidance first — then a CRM that stays simple, stable, and clear every day.',
};

export const PARTNERSHIP_PILLARS: PartnershipPillar[] = [
  {
    id: 'partner',
    title: 'We work as a partner',
    iconKey: 'team',
    description:
      'Not a boxed product you fight alone. We shape the workspace with you and stay involved as your process grows.',
  },
  {
    id: 'digitalize',
    title: 'We help you go digital',
    iconKey: 'book',
    description:
      'Moving off spreadsheets is a change. We educate your team and guide adoption so digitalization actually sticks.',
  },
  {
    id: 'support',
    title: '24/7 support',
    iconKey: 'support',
    description:
      'Questions during a call or a late-night close do not wait for Monday. Real people when your business is selling.',
  },
  {
    id: 'reliable',
    title: 'Reliable for everyday use',
    iconKey: 'check',
    description:
      'Built for stability so your team focuses on customers — not workarounds, fire drills, or wondering if the system will behave.',
  },
  {
    id: 'simple',
    title: 'Easy to use',
    iconKey: 'setting',
    description:
      'One clear way to do each job. Less training, fewer mistakes, and less time hunting for where something lives.',
  },
  {
    id: 'clarity',
    title: 'Clear picture, productive features',
    iconKey: 'dashboard',
    description:
      'Dashboards and flows that show what matters — so owners and reps see the full picture and act, not dig through noise.',
  },
  {
    id: 'payment',
    title: 'Flexible, interest-free payments',
    iconKey: 'wallet',
    description:
      'No large upfront cost. Pay 20% to start, then monthly, quarterly, half-yearly, or another schedule — interest-free installments (like EMI, without bank finance or hidden charges).',
  },
];

export const WHEN_TO_MOVE_CRM = {
  eyebrow: 'Readiness',
  title: 'You need a CRM when…',
  lead: 'Not on day one — when scattered tools start costing deals.',
};

export const WHEN_TO_MOVE_POINTS: WhenToMovePoint[] = [
  {
    id: 'spreadsheets',
    headline: 'Spreadsheets and side threads',
    detail: 'Nobody trusts the pipeline.',
  },
  {
    id: 'manager',
    headline: 'Managers chase screenshots',
    detail: 'Reviews become status theater.',
  },
  {
    id: 'quotes',
    headline: 'Quotes live off the deal',
    detail: 'Versions drift. Finance argues.',
  },
  {
    id: 'followups',
    headline: 'Follow-ups live off the record',
    detail: 'Hot leads go cold.',
  },
  {
    id: 'scale',
    headline: 'More reps, same chaos',
    detail: 'Time for one source of truth.',
  },
];

export const FEATURES_SECTION = {
  eyebrow: 'Features',
  title: 'Everything your team needs in one place',
  lead: 'From pipeline to commissions — modules that connect instead of competing spreadsheets.',
};

export const LANDING_FEATURE_CARDS: LandingFeatureCard[] = [
  {
    id: 'pipeline',
    iconKey: 'funnel',
    title: 'Pipeline & leads',
    description: 'Stages, BANT scoring, and a pipeline view your managers can trust in every review.',
  },
  {
    id: 'customers',
    iconKey: 'team',
    title: 'Customers & segments',
    description: 'Contacts, categories, and segment-wise access so the right rep sees the right accounts.',
  },
  {
    id: 'quotes',
    iconKey: 'file',
    title: 'Quotes & documents',
    description: 'Send quotes from the live deal so versions, offerings, and closed revenue stay aligned.',
  },
  {
    id: 'overview',
    iconKey: 'dashboard',
    title: 'Business overview',
    description: 'Performance, tasks, and commissions in dashboards built for owners, not analysts.',
  },
  {
    id: 'execution',
    iconKey: 'check',
    title: 'Follow-ups',
    description: 'Callbacks, reminders, and activity tied to the contact record — not scattered apps.',
  },
  {
    id: 'context',
    iconKey: 'profile',
    title: 'Account context',
    description: 'Kundali and linked activity give the full customer story in one click.',
  },
];

export const FEATURE_BUSINESS_GROUPS: FeatureBusinessGroup[] = [
  {
    id: 'pulse',
    label: 'Business pulse',
    rows: [
      {
        id: 'performance',
        iconKey: 'dashboard',
        businessAsk: 'How is the business performing right now?',
        modules: ['Business Overview', 'Task Dashboard'],
      },
      {
        id: 'commissions',
        iconKey: 'dollar',
        businessAsk: 'What do we owe agents and what closed this period?',
        modules: ['Commissions', 'Business Overview'],
      },
    ],
  },
  {
    id: 'revenue',
    label: 'Revenue motion',
    rows: [
      {
        id: 'pipeline',
        iconKey: 'funnel',
        businessAsk: 'What is in the pipeline and what will close?',
        modules: ['Leads & pipeline', 'BANT scoring', 'Pipeline stages'],
      },
      {
        id: 'quotes',
        iconKey: 'file',
        businessAsk: 'What did we quote, send, and win?',
        modules: ['Documents & quotes', 'Offerings & categories'],
      },
      {
        id: 'campaigns',
        iconKey: 'sound',
        businessAsk: 'Which programs are feeding qualified leads?',
        modules: ['Campaigns'],
      },
    ],
  },
  {
    id: 'customers',
    label: 'Customer intelligence',
    rows: [
      {
        id: 'who',
        iconKey: 'team',
        businessAsk: 'Who are we selling to and how are they segmented?',
        modules: ['Contacts', 'Customer segments', 'Contact categories'],
      },
      {
        id: 'context',
        iconKey: 'profile',
        businessAsk: 'What is the full story on this account in one click?',
        modules: ['Kundali', 'Linked leads & activity'],
      },
      {
        id: 'territory',
        iconKey: 'user-switch',
        businessAsk: 'Which reps see which customers?',
        modules: ['Segment-wise users'],
      },
    ],
  },
  {
    id: 'execution',
    label: 'Daily execution',
    rows: [
      {
        id: 'tasks',
        iconKey: 'check',
        businessAsk: 'What must the team do today so deals do not stall?',
        modules: ['Follow-ups', 'Task Dashboard'],
      },
    ],
  },
  {
    id: 'control',
    label: 'Control & fit',
    rows: [
      {
        id: 'access',
        iconKey: 'safety',
        businessAsk: 'Who can see and change what in our org?',
        modules: ['Users & roles', 'CRM setup'],
      },
    ],
  },
];

export const COMMITMENTS_SECTION = {
  eyebrow: 'Commitments',
  title: 'What we promise every customer',
  lead: 'How we work with you after you sign — clear expectations, not fine print buried in a contract.',
};

export const COMMITMENTS = [
  {
    title: 'Honest timelines',
    iconKey: 'rocket' as FeatureIconKey,
    text: 'Scoped requests ship within ten business days. We quote realistic dates and keep you updated when plans shift.',
  },
  {
    title: 'Your process, your workspace',
    iconKey: 'customize' as FeatureIconKey,
    text: 'Stages, segments, and permissions shaped with your team — not a one-size template you have to fight.',
  },
  {
    title: 'Humans when it matters',
    iconKey: 'support' as FeatureIconKey,
    text: '24/7 support from people who know the product — especially when a deal is on the line, not a ticket queue.',
  },
];

export const PAYMENT_PLAN_HIGHLIGHTS = [
  'No large upfront cost — begin with 20% down',
  'Choose monthly, quarterly, half-yearly, or another schedule that fits your cash flow',
  'Interest-free installments — works like straightforward EMI, but it is your payment plan, not bank debt or hidden finance charges',
];

export const ACCESS_SECTION = {
  eyebrow: 'Pricing',
  title: 'Request a demo',
  lead: 'See the product and get a plan sized for your team. Flexible payment: 20% to start, then pay the rest on your schedule.',
  primaryLabel: 'Request a demo',
};
